import { Injectable } from '@angular/core';
import { Imposition, ApiError } from '@api/models';
import { Subscription, zip } from 'rxjs';
import { DepositService } from '@core/services/deposit/deposit.service';
import { switchMap, map, tap, delay, take } from 'rxjs/operators';
import { GlobalPositionService } from '@core/services/global-position/global-position.service';
import { findDeposit, findAccount } from '@core/utils';
import { DictionaryService } from '@core/services/dictionary/dictionary.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environment/environment';
import { DatePipe } from '@angular/common';
import { RenewImpositionOtp } from '@api/models/response-renew-imposition';

@Injectable({
  providedIn: 'root'
})

export class ImpositionService {
  readonly state$ = this.createState();
  readonly impositionConditions$ = this.dictionaryService.getImpositionConditions();

  private imposition: Imposition;
  private referenceOTP: string;
  private subs = new Subscription();

  constructor(
    private depositService: DepositService,
    private globalPositionService: GlobalPositionService,
    private dictionaryService: DictionaryService,
    private http: HttpClient
  ) {}

  initImposition(imposition: Imposition) {
    this.imposition = imposition;
  }

  fetchGlobalPosition() {
    this.globalPositionService.fetchGlobalPosition();
  }

  generateOtp(onSuccess?: () => void, onError?: (error: ApiError) => void) {
    this.referenceOTP = null;
    const productCode = JSON.parse(localStorage.getItem('productInfo')).impositionConditions.productCode;
    const IBAN = JSON.parse(localStorage.getItem('productInfo')).iban;

    const generateOtp = (imposition: Imposition) =>
      this.depositService.generateCreateImpositionOTP({ ...imposition });

    this.subs.add(
      this.state$
        .pipe(
          take(1),
          switchMap((state) =>
            generateOtp({
              ...this.imposition,
              depositAgreement: state?.deposit
              .filter((deposit) => deposit.ProductCode === productCode && deposit.IBAN === IBAN)[0]?.Acuerdo
            })
          )
        )
        .subscribe(
          ({ otpId }) => {
            this.referenceOTP = otpId;
            onSuccess?.();
          },
          (error) => onError?.(error)
        )
    );
  }

  createImposition(secretKey: string, onSuccess?: () => void, onError?: (error: ApiError) => void) {
    const productCode = JSON.parse(localStorage.getItem('productInfo')).impositionConditions.productCode;
    const IBAN = JSON.parse(localStorage.getItem('productInfo')).iban;

    this.subs.add(
      this.state$
        .pipe(
          take(1),
          switchMap((state) =>
            this.depositService.createImposition({
              ...this.imposition,
              depositAgreement: state?.deposit
              .filter((deposit) => deposit.ProductCode === productCode && deposit.IBAN === IBAN)[0]?.Acuerdo,
              otp: { secretKey, otpId: this.referenceOTP },
            })
          )
        )
        .subscribe(
          () => {
            onSuccess?.();
          },
          (error) => {
            onError?.(error);
          }
        )
    );
  }

  impositionConditionsDoc(docName) {
    const pipe = new DatePipe('es-ES');
    const downloadDate = {Fecha: pipe.transform(new Date(), 'dd/MM/yyyy')};
    const url = `${environment.apiBasePath}${environment.endpoints.deposit.downloadImpositionConditionDoc}/${docName}`;
    return this.http.post<any>(url, downloadDate, { responseType: 'text' as any });
  }

  destroy() {
    this.subs.unsubscribe();
  }

  private createState() {
    return zip(
      this.globalPositionService.globalPosition$,
      this.globalPositionService.globalPosition$.pipe(findDeposit()),
      this.globalPositionService.globalPosition$.pipe(findAccount())
    ).pipe(
      map(([globalPosition, deposit, account]) => ({
        globalPosition,
        deposit,
        account,
      }))
    );
  }
  generateOtpRenewImposition(previousRenewal, onSuccess?: () => void, onError?: (error: ApiError) => void ) {

    this.referenceOTP = null;
    this.subs.add(
      this.state$
      .pipe(
        take(1),
        switchMap(() => this.depositService.generateRenewalOtp(previousRenewal)),
        tap((response: any) => {
        if (response?.otpId) {
          this.referenceOTP = response.otpId;
        }
        }),

      )
      .subscribe({
        next: () => onSuccess?.(),
        error: (error: ApiError) => onError?.(error)
      })
    );
  }
  processImpositionRenewal(renewImposition: RenewImpositionOtp, onSuccess?: () => void, onError?: (error: ApiError) => void) {

    this.subs.add(
      this.state$
        .pipe(
          take(1),
          switchMap(() =>
            this.depositService.generateRenewal({
              otp: {
                otpId: this.referenceOTP,
                secretKey: renewImposition.otp?.secretKey,
              },
              depositAgreement: renewImposition.depositAgreement,
              impositionAgreement: renewImposition.impositionAgreement,
              renewal: renewImposition.renewal,
            })
          )
        )
        .subscribe(
          () => {
            onSuccess?.();
          },
          (error) => {
            onError?.(error);
          }
        )
    );

  }
  updateSalesforceImposition(body: any,  onSuccess?: (resp) => void, onError?: (error: ApiError) => void){
    //Método creado para actualizar la imposición en SF tomando en cuenta la respuesta de RSI, se tratan los errores en un método diferente

    this.subs.add(
      this.depositService.updateSalesforceImposition(body).subscribe(
        (resp)=>{
          onSuccess?.(resp)
        },
        (error)=>{
          onError?.(error)
        }
      )
    )
  }
}
